import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import {
  costByGroups,
  costCurveApi,
  FIXED_COST_GROUPS,
  type CostGroupToggle as Group,
} from "@/api/costCurve";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { fmt } from "@/lib/format";

export function CostBreakdownCard({ date }: { date?: string }) {
  const { t } = useTranslation();
  // За замовчуванням — лише газ: собівартість палива без постійних витрат.
  const [enabled, setEnabled] = useState<Record<Group, boolean>>({
    gas: true,
    rent: false,
    service: false,
    consumables: false,
    other: false,
  });

  const cc = useQuery({
    queryKey: ["cost-curve", date ?? "today"],
    queryFn: () => costCurveApi.get(date),
  });

  /** Кнопки лише для статей, що реально є в «Дані» на цей місяць. */
  const groups: Group[] = useMemo(
    () => [
      "gas",
      ...FIXED_COST_GROUPS.filter((g) => (cc.data?.fixed_uah_per_hour[g] ?? 0) !== 0),
    ],
    [cc.data],
  );

  const rows = useMemo(() => (cc.data ? costByGroups(cc.data, enabled) : []), [cc.data, enabled]);
  const powerMax = cc.data?.power_max_kw ?? 0;
  const pct = (kw: number) => (powerMax > 0 ? Math.round((kw / powerMax) * 100) : 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("cost_curve.breakdown_title")}</CardTitle>
        <CardDescription>{t("cost_curve.breakdown_hint")}</CardDescription>
        <div className="flex items-center gap-2 flex-wrap pt-2">
          {groups.map((g) => (
            <Button
              key={g}
              size="sm"
              variant={enabled[g] ? "default" : "outline"}
              onClick={() => setEnabled((s) => ({ ...s, [g]: !s[g] }))}
              title={t("finance.cost_toggle_hint")}
            >
              {enabled[g] ? "− " : ""}
              {t(`finance.cost_${g}`)}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {cc.isLoading ? (
          <div className="text-sm text-slate-500">{t("common.loading")}</div>
        ) : cc.isError || !cc.data ? (
          <div className="text-sm text-slate-500">{t("cost_curve.unavailable")}</div>
        ) : (
          <div className="grid grid-cols-3 gap-3 text-sm">
            {rows.map((p) => (
              <div key={p.power_kw} className="rounded-lg bg-slate-50 p-3">
                <div className="text-xs text-slate-500">
                  {pct(p.power_kw)}% — {fmt(p.power_kw)} {t("common.kw")}
                </div>
                <div className="font-mono text-lg">{fmt(p.cost_uah_per_mwh)}</div>
                <div className="text-xs text-slate-500">{t("cost_curve.unit")}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
